import React, { useState } from 'react';
import { Box, IconButton, Grid, Paper, Tooltip } from '@mui/material';
import { ArrowBack, ArrowForward } from '@mui/icons-material';
import { format, addMonths, subMonths, startOfMonth, startOfWeek, addDays, getWeek, subDays } from 'date-fns';
import MaterialTypography from '../../../../components/MaterialTypography';
import { useNavigate } from 'react-router-dom';
import MaterialButton from '../../../../components/MaterialButton';

const CalendarWidget = ({ data, onMonthChange }) => {
    const navigate = useNavigate();
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState(new Date());

    const nextMonth = () => {
        const newMonth = addMonths(currentMonth, 1);
        setCurrentMonth(newMonth);
        if (onMonthChange) {
            onMonthChange(newMonth);
        }
    };

    const prevMonth = () => {
        const newMonth = subMonths(currentMonth, 1);
        setCurrentMonth(newMonth);
        if (onMonthChange) {
            onMonthChange(newMonth);
        }
    };

    const onDateClick = (day) => {
        setSelectedDate(day);
    };

    const getWeekNumber = (date) => {
        return getWeek(date, { weekStartsOn: 1 });
    }

    const renderHeader = () => {
        return (
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2.5}>
                <Box
                    sx={{
                        backgroundColor: '#bfd0e3',
                        borderRadius: '20px',
                        padding: '4px 16px',
                        marginRight: '10px'
                    }}
                >
                    <MaterialTypography variant="body2" fontWeight="bold" color="text.primary">
                        {format(currentMonth, 'MMMM')}
                    </MaterialTypography>
                </Box>
                <Box>
                    <IconButton onClick={prevMonth} size="small">
                        <ArrowBack />
                    </IconButton>
                    <IconButton onClick={nextMonth} size="small">
                        <ArrowForward />
                    </IconButton>
                </Box>
                <MaterialButton
                    variant="contained"
                    size="small"
                    sx={{
                        backgroundColor: '#bfd0e3',
                        color: '#fff',
                        textTransform: 'none',
                        borderRadius: '20px',
                        padding: '6px 20px',
                        fontSize: '0.8rem',
                        '&:hover': {
                            backgroundColor: '#333'
                        }
                    }}
                    onClick={handleNavigateTask}
                >
                    Go to Task List
                </MaterialButton>
            </Box>
        );
    };

    const renderDays = () => {
        const days = [];
        const dateFormat = "EEE";
        const startDate = startOfWeek(currentMonth, { weekStartsOn: 1 });

        for (let i = 0; i < 7; i++) {
            days.push(
                <Grid size={1} key={i}>
                    <MaterialTypography
                        variant="caption"
                        color="text.secondary"
                        align="center"
                        display="block"
                        sx={{
                            backgroundColor: '#EFEFEF',
                            borderRadius: '4px',
                            padding: '2px 0'
                        }}
                    >
                        {format(addDays(startDate, i), dateFormat)}
                    </MaterialTypography>
                </Grid>
            );
        }
        // Empty slot for week number column header
        days.push(<Grid size={1} key="empty-header" />);

        return <Grid container spacing={1} mb={1} columns={8}>{days}</Grid>;
    };

    const renderCells = () => {
        const monthStart = startOfMonth(currentMonth);
        const startDate = startOfWeek(monthStart, { weekStartsOn: 1 });

        // Force 6 weeks (42 days) to keep height consistent
        // 0 to 41 = 42 days
        const endDate = addDays(startDate, 41);

        const dateFormat = "d";
        const rows = [];
        let days = [];
        let day = startDate;
        let formattedDate = "";

        while (day <= endDate) {
            // Render 7 days
            for (let i = 0; i < 7; i++) {
                formattedDate = format(day, dateFormat);
                const cloneDay = day;
                const isSelected = selectedDate && format(day, 'yyyy-MM-dd') === format(selectedDate, 'yyyy-MM-dd');
                const isCurrentMonth = format(day, 'MM') === format(monthStart, 'MM');

                const formattedDateKey = format(day, 'yyyy-MM-dd');
                const dayData = data?.find(d => d.date === formattedDateKey);
                const hours = dayData?.hours || 0;
                const percentage = Math.min((hours / 8) * 100, 100);

                days.push(
                    <Grid size={1} key={day.toString()} sx={{ textAlign: 'center' }}>
                        <Tooltip title={`${hours} Hours`} placement="top" arrow>
                            <Box
                                onClick={() => onDateClick(cloneDay)}
                                sx={{
                                    width: '35px',
                                    height: '35px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    margin: '0 auto',
                                    cursor: 'pointer',
                                    borderRadius: '20%',
                                    background: (hours > 0
                                        ? `linear-gradient(to right, #bfd0e3 ${percentage}%, transparent ${percentage}%)`
                                        : 'transparent'),
                                    border: isSelected ? '1.5px solid #bfd0e3' : 'none',
                                    color: (isCurrentMonth ? 'text.primary' : 'text.disabled'),
                                    fontWeight: isSelected ? 'bold' : 'normal',
                                    '&:hover': {
                                        backgroundColor: '#eee'
                                    }
                                }}
                            >
                                <MaterialTypography variant="body2">
                                    {formattedDate}
                                </MaterialTypography>
                            </Box>
                        </Tooltip>
                    </Grid>
                );
                day = addDays(day, 1);
            }

            // Render Week Number (8th column)
            // Use date from the end of the week (Sunday) to calculate week number
            const sunday = subDays(day, 1);
            const weekNumber = getWeekNumber(sunday);

            days.push(
                <Grid size={1} key={`week-${weekNumber}`} sx={{ textAlign: 'center' }}>
                    <Box
                        sx={{
                            backgroundColor: '#E0E0E0',
                            borderRadius: '10px',
                            padding: '2px 2px',
                            display: 'inline-block',
                            minWidth: '32px'
                        }}
                    >
                        <MaterialTypography variant="caption" color="text.secondary" fontSize="0.7rem">
                            W{weekNumber}
                        </MaterialTypography>
                    </Box>
                </Grid>
            );

            rows.push(
                <Grid container spacing={1} key={`row-${day}`} alignItems="center" mb={1} columns={8}>
                    {days}
                </Grid>
            );
            days = [];
        }
        return <Box>{rows}</Box>;
    };

    const handleNavigateTask = () => {
        navigate('/project-task');
    }

    return (
        <Paper
            elevation={0}
            sx={{
                padding: 2,
                backgroundColor: '#e4e6f2',
                borderRadius: 4,
                width: '100%',
            }}
        >
            {renderHeader()}
            {renderDays()}
            {renderCells()}
        </Paper>
    );
};

export default CalendarWidget;
